if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      this.swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');

      this.currentIndex = 0;
      this.slides = [];
      this.slidesPerView = 1;
      this.spaceBetween = 2;

      this.handleResize = this.handleResize.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }

      this.initSlider();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);

      if (this.sliderWrapper) {
        this.sliderWrapper.removeEventListener('focusin', this.handleFocusIn);
      }
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');

      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML = wrapper.innerHTML;
            this.parent.classList.remove('hidden');
            this.initSlider();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    initSlider() {
      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      if (!this.sliderWrapper) return;

      this.slides = Array.from(this.sliderWrapper.children);
      if (!this.slides.length) return;

      this.style.overflow = 'hidden';

      this.sliderWrapper.style.display = 'flex';
      this.sliderWrapper.style.transition = 'transform 0.4s ease';
      this.sliderWrapper.style.willChange = 'transform';

      this.setupA11y();
      this.setupNavigation();
      this.updateSlider();

      window.addEventListener('resize', this.handleResize);
      this.sliderWrapper.addEventListener('focusin', this.handleFocusIn);
    }

    setupA11y() {
      this.sliderWrapper.setAttribute('aria-live', 'polite');
      this.sliderWrapper.id = `swiper-wrapper-${this.dataset.sectionId}`;

      this.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
        slide.dataset.index = i;
      });
    }

    setupNavigation() {
      const prevBtn = this.querySelector('.swiper-button--prev');
      const nextBtn = this.querySelector('.swiper-button--next');

      if (prevBtn) {
        prevBtn.setAttribute('aria-label', 'Previous slide');
        prevBtn.setAttribute('aria-controls', this.sliderWrapper.id);

        prevBtn.onclick = () => {
          this.prevSlide();
        };
      }

      if (nextBtn) {
        nextBtn.setAttribute('aria-label', 'Next slide');
        nextBtn.setAttribute('aria-controls', this.sliderWrapper.id);

        nextBtn.onclick = () => {
          this.nextSlide();
        };
      }
    }

    handleResize() {
      this.updateSlider();
    }

    updateSlider() {
      const width = window.innerWidth;

      this.slidesPerView = 1;
      this.spaceBetween = this.swiperOptions.spaceBetweenMobile || 2;

      if (width >= 1100) {
        this.slidesPerView = 4;
        this.spaceBetween = this.swiperOptions.spaceBetweenDesktop || 2;
      } else if (width >= 750) {
        this.slidesPerView = 3;
        this.spaceBetween = this.swiperOptions.spaceBetweenDesktop || 2;
      } else {
        this.slidesPerView = 'auto';
      }

      if (this.slidesPerView === 'auto') {
        this.slides.forEach(slide => {
          slide.style.flex = '0 0 auto';
          slide.style.marginRight = `${this.spaceBetween}px`;
        });
      } else {
        const slideWidth = `calc((100% - ${
          (this.slidesPerView - 1) * this.spaceBetween
        }px) / ${this.slidesPerView})`;

        this.slides.forEach(slide => {
          slide.style.flex = `0 0 ${slideWidth}`;
          slide.style.marginRight = `${this.spaceBetween}px`;
        });
      }

      this.goToSlide(this.currentIndex, false);
    }

    getMaxIndex() {
      if (this.slidesPerView === 'auto') {
        return Math.max(this.slides.length - 1, 0);
      }

      return Math.max(this.slides.length - this.slidesPerView, 0);
    }

    goToSlide(index, animate = true) {
      const maxIndex = this.getMaxIndex();

      this.currentIndex = Math.max(0, Math.min(index, maxIndex));

      if (!animate) {
        this.sliderWrapper.style.transition = 'none';
      } else {
        this.sliderWrapper.style.transition = 'transform 0.4s ease';
      }

      const slide = this.slides[this.currentIndex];
      if (!slide) return;

      const offset = slide.offsetLeft;

      this.sliderWrapper.style.transform = `translateX(-${offset}px)`;

      if (!animate) {
        requestAnimationFrame(() => {
          this.sliderWrapper.style.transition = 'transform 0.4s ease';
        });
      }
    }

    nextSlide() {
      this.goToSlide(this.currentIndex + 1);
    }

    prevSlide() {
      this.goToSlide(this.currentIndex - 1);
    }

    handleFocusIn(e) {
      const slide = e.target.closest('.swiper-slide');

      if (!slide) return;

      const slideIndex = Number(slide.dataset.index);

      if (slideIndex === 0 || slideIndex === this.slides.length - 1) {
        this.goToSlide(slideIndex, false);
        return;
      }

      const slideRect = slide.getBoundingClientRect();
      const sliderRect = this.getBoundingClientRect();

      const isVisible =
        slideRect.left >= sliderRect.left &&
        slideRect.right <= sliderRect.right;

      if (!isVisible) {
        this.goToSlide(slideIndex, false);
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}